import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Camera, Compass, Star } from "lucide-react";
import PointsBadge from "../PointsBadge";
import DefaultAvatarPlaceholder from "../placeholders/DefaultAvatarPlaceholder";
import { resolveAvatarUrl, resolveUserDisplayName } from "../../utils/userIdentity";
import { formatLocalizedMonthYear } from "../../utils/dateLocalization";

const ProfileHeader = ({
  profile,
  ratingStats,
  reviewsCount = 0,
  onViewReviews,
  isOwner = false,
  onEditPhoto,
  onMoreNew,
}) => {
  const { t, i18n, ready: i18nReady } = useTranslation(["profile", "common"]);

  const safeT = useMemo(() => {
    return (key, fallback = key) => {
      if (!i18nReady) return fallback;
      const result = t(key);
      return result === key ? fallback : result;
    };
  }, [t, i18nReady]);

  const formatLocation = (location) => {
    if (!location || typeof location !== "object") return null;
    const parts = [location.cell, location.sector, location.district].filter(Boolean);
    return parts.length ? parts.join(", ") : null;
  };

  const joinedDate = formatLocalizedMonthYear({
    value: profile?.createdAt,
    locale: i18n?.language,
    translate: (key, fallback) => {
      const result = t(key, { ns: "common" });
      return result === key ? fallback : result;
    },
  });

  const location = formatLocation(profile?.location);
  const username = profile?.username || resolveUserDisplayName(profile);
  const displayName = profile?.displayName || profile?.name || username;
  const avatarUrl = resolveAvatarUrl(profile);
  const hasCustomAvatar = Boolean(profile?.photoURL || profile?.avatar || profile?.photo);
  const [avatarLoadFailed, setAvatarLoadFailed] = useState(false);
  const totalPoints = Number(profile?.points || 0);
  const ratingAverage = Number(ratingStats?.avg || 0);
  const normalizedRating = Number.isFinite(ratingAverage)
    ? Math.max(0, Math.min(5, ratingAverage))
    : 0;
  const ratingValue = normalizedRating.toFixed(1);
  const starFillPercents = Array.from({ length: 5 }, (_, index) => {
    return Math.max(0, Math.min(1, normalizedRating - index)) * 100;
  });
  useEffect(() => {
    setAvatarLoadFailed(false);
  }, [avatarUrl]);

  return (
    <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-gray-200 dark:border-neutral-800 shadow-sm p-4 sm:p-6">
      <div className="flex items-center gap-4 sm:gap-5">
        <div className="min-w-0 flex flex-1 items-center gap-3 sm:gap-4">
          <button
            type="button"
            onClick={() => {
              if (isOwner && onEditPhoto) onEditPhoto();
            }}
            className={`relative h-20 w-20 sm:h-24 sm:w-24 rounded-full overflow-hidden bg-gray-200 dark:bg-neutral-700 flex-shrink-0 border border-gray-200 dark:border-neutral-700 ${isOwner ? "cursor-pointer focus:outline-none focus:ring-2 focus:ring-green-600" : "cursor-default"}`}
            aria-label={isOwner ? safeT("profile.edit.aria.uploadAvatar", "Upload profile photo") : displayName}
            disabled={!isOwner || !onEditPhoto}
          >
            {hasCustomAvatar && !avatarLoadFailed ? (
              <img
                src={avatarUrl}
                alt={displayName}
                className="w-full h-full object-cover"
                onError={() => setAvatarLoadFailed(true)}
              />
            ) : (
              <DefaultAvatarPlaceholder />
            )}
            {isOwner && (
              <span className="absolute bottom-0 right-0 w-7 h-7 rounded-full bg-green-600 text-white border-2 border-white dark:border-neutral-900 flex items-center justify-center">
                <Camera className="w-3.5 h-3.5" />
              </span>
            )}
          </button>

          <div className="min-w-0 text-left">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white break-words">{displayName}</h1>
            <p className="mt-0.5 text-sm font-medium text-green-700 dark:text-green-400 break-all">@{username}</p>
            {joinedDate && (
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                {safeT("profile.stats.joined", "Joined")} {joinedDate}
              </p>
            )}
            {location && (
              <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">{location}</p>
            )}
            {profile?.bio && (
              <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-gray-700 dark:text-gray-200">{profile.bio}</p>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-row gap-3 mt-5">
        <div className="rounded-xl border border-gray-200 dark:border-neutral-800 p-4 bg-gray-50 dark:bg-neutral-800/50 flex items-center justify-center gap-2 flex-1 min-w-0">
          <PointsBadge points={totalPoints} size="sm" />
          <span className="text-sm font-semibold text-gray-900 dark:text-white whitespace-nowrap truncate">{totalPoints} IP</span>
        </div>

        {onMoreNew ? (
          <Link
            to="/more"
            className="rounded-xl border border-gray-200 dark:border-neutral-800 p-4 bg-gray-50 dark:bg-neutral-800/50 hover:bg-gray-100 dark:hover:bg-neutral-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-600 flex items-center justify-center gap-2 flex-1 min-w-0"
          >
            <Compass className="h-5 w-5 text-green-700 dark:text-green-400 flex-shrink-0" aria-hidden="true" />
            <span className="text-sm font-semibold text-gray-900 dark:text-white whitespace-nowrap truncate">
              {safeT("profile.moreNew", "More new")}
            </span>
          </Link>
        ) : null}
      </div>

      <button
        onClick={onViewReviews}
        className="mt-3 w-full rounded-xl border border-gray-200 dark:border-neutral-800 p-4 bg-gray-50 dark:bg-neutral-800/50 hover:bg-gray-100 dark:hover:bg-neutral-800"
      >
        <div className="flex items-center justify-center gap-6">
              <div className="text-center">
                <p className="text-xs text-gray-500 dark:text-gray-400">{safeT("profile.stats.rating", "Rating")}</p>
                <div className="mt-1 inline-flex items-center gap-2" aria-label={`${ratingValue} out of 5`}>
                  <span className="text-2xl font-semibold text-gray-900 dark:text-white leading-none">{ratingValue}</span>
                  <div className="flex items-center gap-0.5" aria-hidden="true">
                    {starFillPercents.map((fillPercent, index) => (
                      <span key={`rating-star-${index}`} className="relative w-4 h-4 inline-flex items-center justify-center">
                        <Star className="w-4 h-4 text-gray-300 dark:text-neutral-600" />
                        <span className="absolute inset-0 overflow-hidden" style={{ width: `${fillPercent}%` }}>
                          <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                        </span>
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              <div className="w-px h-10 bg-gray-200 dark:bg-neutral-700" />
              <div className="text-center">
                <p className="text-xs text-gray-500 dark:text-gray-400">{safeT("profile.stats.reviews", "Reviews")}</p>
                <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">{reviewsCount}</p>
              </div>
        </div>
      </button>
    </div>
  );
};

export default ProfileHeader;